<template>
  <section class="content-area__content login" :class="{ embedded: inFrame }">
    <vue-title :title="$t('identityProvider:login:title')"></vue-title>

    <div id="downtime-message-container" class="downtime-container" role="alert" data-test-element="active-downtime-message" v-if="isActiveDTM">
      <span class="active-downtime-message">
        <i class="material-icons notification-icon" data-test-element="downtime-message-notification">notifications</i>
        <label class="message-label" data-test-element="downtime-message-label">{{ model.activeDowntimeMessage }}</label>
      </span>
    </div>

    <bulletin-component ref="bulletin"></bulletin-component>

    <h1 class="section-title" v-if="!inFrame">{{ $t('identityProvider:login:title') }}</h1>

    <Form v-slot="{ setErrors, setFieldError, validate }">
      <form ref="form" action="authentication" @submit.prevent="validateBeforeSubmit(validate, setErrors)">
        <!-- Summary (server-driven) -->
        <div class="validation-summary-errors alert alert-danger fade show"
             role="alert"
             data-valmsg-summary="true"
             data-test-element="idp-alert-danger-generic"
             v-if="model.showError && !hasAnyFieldErrors">
          <i class="material-icons">warning</i>
          <span v-if="model.errorMessage">{{ model.errorMessage }}</span>
          <span v-else>{{ genericErrorText }}</span>
        </div>

        <div class="validation-summary-errors alert alert-danger fade show"
             role="alert"
             data-valmsg-summary="true"
             data-test-element="idp-alert-danger-validation"
             v-if="hasAnyFieldErrors">
          <i class="material-icons">error</i>
          <span class="alert-message" data-test-element="alert-message">{{ $t('validation:summary') }}</span>
        </div>

        <div class="panel login">
          <div class="login-form-controls">
            <!-- Username -->
            <div class="form-group">
              <label>
                <span class="label">{{ $t('shared:fields:username') }}</span>
                <Field name="Username" :rules="'required'" v-model="model.username" v-slot="{ field, meta }">
                  <input v-bind="field"
                         data-test-element="Username"
                         class="control"
                         v-focus
                         :class="{ 'input-validation-error': submitted && !meta.valid }"
                         autocomplete="off"
                         aria-required="true"
                         :aria-invalid="(submitted && !meta.valid) ? 'true' : 'false'"
                         aria-describedby="username-error" />
                </Field>
              </label>
              <ErrorMessage v-if="submitted" name="Username" v-slot="{ message }">
                <span class="field-validation-error" id="username-error" role="alert">{{ message }}</span>
              </ErrorMessage>
            </div>

            <!-- Password -->
            <div class="form-group">
              <label>
                <span class="label">{{ $t('shared:fields:password') }}</span>
                <div class="password-container">
                  <Field name="Password" :rules="'required'" v-model="model.password" v-slot="{ field, meta }">
                    <input v-bind="field"
                           :type="showPassword ? 'text' : 'password'"
                           data-test-element="Password"
                           class="control"
                           :class="{ 'input-validation-error': submitted && !meta.valid }"
                           autocomplete="off"
                           aria-required="true"
                           :aria-invalid="(submitted && !meta.valid) ? 'true' : 'false'"
                           aria-describedby="password-error" />
                  </Field>
                  <span class="toggle-password" @click="togglePasswordVisibility">
                    <i class="material-icons">{{ showPassword ? 'visibility' : 'visibility_off' }}</i>
                  </span>
                </div>
              </label>
              <ErrorMessage v-if="submitted" name="Password" v-slot="{ message }">
                <span class="field-validation-error" id="password-error" role="alert">{{ message }}</span>
              </ErrorMessage>
            </div>

            <div class="form-actions">
              <button type="submit" class="button" id="logonButton" :disabled="disabled">
                {{ $t('identityProvider:login:submit') }}
              </button>
            </div>
          </div>

          <!-- Ancillary actions -->
          <div class="other-actions login">
            <div class="action" v-if="model.enrollUrlEnabled">
              <div class="prompt" tabindex="0">{{ $t('identityProvider:login:enrollPrompt') }}</div>
              <a class="link" v-if="model.enrollUrl && !isActiveDTM" data-test-element="enroll-user" :href="model.enrollUrl" @click="clickedLink">
                {{ $t('identityProvider:login:enroll') }}
              </a>
              <span class="link disable-links" data-test-element="enroll-user" v-if="isActiveDTM">{{ $t('identityProvider:login:enroll') }}</span>
            </div>

            <div class="action" v-if="model.forgottenPasswordEnabled">
              <div class="prompt" tabindex="0">{{ forgottenPasswordPrompt }}</div>
              <a class="link" v-if="model.forgottenPasswordUrl && !isActiveDTM" data-test-element="reset-pass" @click="clickedLink" :href="model.forgottenPasswordUrl">
                {{ forgottenPasswordLink }}
              </a>
              <span class="link disable-links" data-test-element="reset-pass" v-if="isActiveDTM">{{ forgottenPasswordLink }}</span>
            </div>

            <br />
            <div class="action" v-if="model.showLanguagePreference">
              <a data-test-element="idp-switchLanguage" @click.prevent="switchLanguage" href="authentication/SwitchLanguage" class="switchLanguage link">
                {{ $t('shared:action:switchLanguage') }}
              </a>
            </div>
          </div>
        </div>
      </form>
    </Form>
  </section>
</template>

<script>
  import redirectHandler from 'utils/redirect';
  import bulletinMessage from './bulletin-message';
  import { Form, Field, ErrorMessage } from 'vee-validate';
  import validation, { applyServerErrorsFromAxios, focusFirstInvalid } from 'utils/validation-mixin';
  import { fmt } from 'utils/i18n-format';

  var activeDTM = false;
  function getIOBlackBoxValue() {
    return document.getElementById("ioBlackBox")?.value ?? null;
  }

  export default {
    name: 'Login',
    components: {
      'bulletin-component': bulletinMessage,
      Form,
      Field,
      ErrorMessage
    },
    mixins: [validation],
    data: () => ({
      messagesReady: false,
      model: {
        username: null,
        password: null,
        returnUrl: null,
        IOBlackBox: null,
        enrollUrl: null,
        activeDowntimeMessage: null,
        alert: null
      },
      disabled: false,
      antiforgery: null,
      isActiveDTM: false,
      showPassword: false,
      hasAnyFieldErrors: false,
      submitted: false
    }),

    computed: {
      inFrame() {
        let inFrame;
        try { inFrame = window.self !== window.top; } catch (e) { inFrame = true; }
        return inFrame && this.$displayMode === "popup";
      },
      genericErrorText() {
        const template = this.$t('identityProvider:login:genericError') || '';
        const supportName = this.$t('identityProvider:tenant:supportName');
        const supportPhone = this.$t('identityProvider:tenant:customerServicePhoneNumber');
        return fmt(template, supportName, supportPhone);
      },
      forgottenPasswordPrompt() {
        const prompt = this.$t('identityProvider:login:forgotPasswordPrompt');
        const label = this.$t('shared:fields:password');
        return `${prompt} ${String(label).toLowerCase()}?`;
      },
      forgottenPasswordLink() {
        const link = this.$t('identityProvider:login:forgotPasswordLink');
        const label = this.$t('shared:fields:password');
        return `${link} ${String(label).toLowerCase()}`;
      }
    },

    methods: {
      togglePasswordVisibility() {
        this.showPassword = !this.showPassword;
      },

      extractServerSummaryMessage(resp) {
        const data = resp?.data;
        if (!data) {
          return null;
        }

        const bag = data.errors || data.ModelState || data.modelState || data;
        const summary = bag?.[''] || bag?.['.'] || bag?._summary || null;
        if (Array.isArray(summary)) {
          return summary.find(Boolean) || null;
        }

        if (typeof summary === 'string' && summary.length > 0) {
          return summary;
        }

        return null;
      },

      async validateBeforeSubmit(validate, setErrors) {
        this.$refs.bulletin?.collapseMessage?.();

        this.model.showError = false;
        this.model.errorMessage = null;
        this.hasAnyFieldErrors = false;
        this.submitted = true;

        const result = await validate();
        const isValid = result === true || result?.valid === true;

        if (isValid) {
          await this.post(setErrors);
        } else {
          this.hasAnyFieldErrors = true;
          focusFirstInvalid(this.$refs.form);
        }
      },

      async post(setErrors) {
        const form = this.$refs.form;
        if (!form) return;

        this.model.IOBlackBox = getIOBlackBoxValue();
        this.disabled = true;

        if (!this.hasAntiforgeryToken()) {
          const template = this.$t('identityProvider:login:genericErrorReload') || '';
          const supportName = this.$t('identityProvider:tenant:supportName');
          const supportPhone = this.$t('identityProvider:tenant:customerServicePhoneNumber');
          const message = fmt(template, supportName, supportPhone);

          if (typeof setErrors === 'function') {
            setErrors({ '': message });
          } else {
            this.model.showError = true;
            this.model.errorMessage = message;
          }
          this.model.password = null;
          this.disabled = false;
          return;
        }

        try {
          const response = await this.$http.post(form.action, this.model);
          if (response.status === 200 && response.data && response.data.redirectUrl) {
            if (this.inFrame) {
              const safeTo = redirectHandler.getSafeRedirectUrl(response.data.redirectUrl);
              if (!safeTo) {
                this.model.password = null;
                return;
              }

              const resolved = this.$router.resolve(safeTo);
              const topUrl = (resolved && Array.isArray(resolved.matched) && resolved.matched.length > 0)
                ? resolved.href
                : safeTo;

              window.top.location.assign(topUrl);
            } else {
              redirectHandler.checkAndRedirect(response, this.$router);
            }
            $('.bulletin-Container').remove();
          }
        } catch (error) {
          const resp = error?.response;
          const summaryMessage = this.extractServerSummaryMessage(resp);

          // Handle form-level server errors (ModelState key = "")
          if (summaryMessage) {
            if (typeof setErrors === 'function') {
              setErrors({});
            }
            this.hasAnyFieldErrors = false;
            this.submitted = false;
            this.model.showError = true;
            this.model.errorMessage = summaryMessage;
            this.model.password = null;
            return;
          }

          if (applyServerErrorsFromAxios(error, { setErrors })) {
            this.hasAnyFieldErrors = true;
            this.model.password = null;
            focusFirstInvalid(this.$refs.form);
            return;
          }
          if (this.validateResponse?.(this.$refs.form, error?.response)) {
            this.model.password = null;
            return;
          }
          throw error;
        } finally {
          this.disabled = false;
        }
      },

      clickedLink(event) {
        if (this.inFrame) {
          window.top.location = event.target.href;
        }
      },

      switchLanguage(event) {
        this.$http.post(event.target.href).then(() => {
          window.location.reload(true);
        });
      },
    },

    async mounted() {
    await this.$nextTick();
    const form = this.$refs.form;
    if (!form) { return; }

    const returnUrl = this.$route.query['returnUrl'];
    const showError = this.$route.query['showError'];
    const errorCode = this.$route.query['errorCode'];

    this.model.returnUrl = returnUrl;

    try {
      const response = await this.$http.get(form.action, {
        params: { returnUrl, showError, errorCode }
      });
      this.model = response.data;

      if (response.data.activeDowntimeMessage) {
        this.isActiveDTM = true;
        activeDTM = true;
        $('#logonButton').attr("disabled", "disabled");
      }
    } catch (error) {
      const bag = error?.response?.data?.errors;
      if (bag) {
        this.model.showError = true;
        this.model.errorMessage = Object.values(bag).flat().find(Boolean) || null;
      }
      throw error;
    } finally {
    }
  }
  };
</script>